/**
 * ReportPlatformCredit — the subtle Gateway Power co-brand line every report
 * cover carries, once: the small G mark followed by "Datacrate · A Gateway
 * Power platform" in the surrounding muted footnote ink. (DOCX / PPTX / the
 * economic brief print the same mark + words on their cover / title page.)
 *
 * Datacrate stays the report's identity (logo, kicker, running folio); this is
 * a platform credit sized like the cover footnotes around it, so it never
 * competes with the title or the content.
 *
 * PLACEMENT — ALWAYS ABSOLUTE, ALWAYS IN SPACE THE COVER ALREADY LEAVES EMPTY,
 * so it adds no height (a cover that fits page 1 still does) and cannot meet
 * other text at any width:
 *   - footnote covers (classic, data-dense, minimal, data-snapshot,
 *     boundary-plate): just below the cover box, left;
 *   - in-flow covers (masthead, editorial-masthead, memo, prospectus,
 *     title-block): right-aligned under the header rule, via
 *     {@link CoverHeaderPlatformCredit};
 *   - frontispiece / dark-instrument / hero-image: the top-right corner of
 *     their padded or inset top.
 * Never a new row at the foot of a cover or the report: a bottom-pinned footer
 * has no slack to give on a full cover, and a row at the report's end pushed
 * trailing content onto an empty page.
 *
 * The mark is the VENDORED brand artwork as an <img> (see
 * `src/lib/reportBrand/gatewayPowerReportBrand.ts`): light artwork on white
 * pages, dark artwork on dark covers / the dark reader. It is decorative —
 * the adjacent text names the company — so it carries `alt=""` and
 * `aria-hidden`: Chromium's tagged PDF leaves it out of the structure tree
 * (an artifact, like the cover's other decorative marks) and the native
 * figure census, which only counts NAMED graphics, does not expect a /Figure
 * for it. The text is a plain <span>, never a <p>, so it stays out of the
 * DOM truth's body-type population (the 10.5 pt body floor measures `p, li`).
 */
import React from 'react';
import type { CSSProperties } from 'react';
import { getThemeReaderMode } from '../../../lib/reportRoleColors';
import {
  REPORT_PLATFORM_CREDIT_LINE,
  gatewayPowerMarkDataUri,
  type GatewayPowerMarkTone,
} from '../../../lib/reportBrand/gatewayPowerReportBrand';

export interface ReportPlatformCreditProps {
  /** Surface the credit sits on — picks the light or the dark brand artwork. */
  tone: GatewayPowerMarkTone;
  /** Resolved text colour (the caller's footnote ink). */
  color?: string;
  fontFamily?: string;
  /** Text size in px; the mark is sized from it. Default 8 (the cover footnote size). */
  fontSizePx?: number;
  className?: string;
  style?: CSSProperties;
  /** Defaults to `report-platform-credit`; covers pass a variant-scoped id. */
  testId?: string;
}

/**
 * The artwork a credit painted on the report paper needs (the light covers):
 * the light artwork, except in the in-app dark reader, which
 * repaints the paper dark. Exports always resolve light (audienceMode
 * `export` pins the reader light). Accepts any theme object — the reader
 * mode rides on it under a symbol key (attachReaderMode).
 */
export function platformCreditToneForTheme(theme: object): GatewayPowerMarkTone {
  return getThemeReaderMode(theme) === 'dark' ? 'on-dark' : 'on-light';
}

/**
 * Zero-height anchor that pins the credit RIGHT-aligned just under a cover's
 * header rule (series bar / eyebrow / title-block top rule). Rendered as the
 * header's next sibling, it occupies no height, and the credit (absolutely
 * positioned inside it) lands in the fixed top gap every in-flow cover keeps
 * between its header and its title (pt-11 / pt-5..12 / pt-12 / margin-top
 * clamp(40px…) / 34mm). So it never adds cover height — a bottom-pinned
 * footer that fits page 1 still fits — never meets other text at any width,
 * and always prints on page 1, even where a cover's own footer spills.
 */
export function CoverHeaderPlatformCredit({
  offsetPx = 6,
  ...credit
}: ReportPlatformCreditProps & { offsetPx?: number }): React.ReactElement {
  return (
    <div className="relative h-0" data-cover-platform-credit-anchor="header">
      <ReportPlatformCredit
        {...credit}
        className={`absolute right-0${credit.className ? ` ${credit.className}` : ''}`}
        style={{ top: offsetPx, ...credit.style }}
      />
    </div>
  );
}

export function ReportPlatformCredit({
  tone,
  color,
  fontFamily,
  fontSizePx = 8,
  className,
  style,
  testId = 'report-platform-credit',
}: ReportPlatformCreditProps): React.ReactElement {
  // The G mark reads at roughly cap height + a little, so it never outweighs
  // the line it introduces.
  const markPx = Math.round(fontSizePx * 1.25);
  return (
    <div
      className={`flex items-center leading-none${className ? ` ${className}` : ''}`}
      style={{ color, fontFamily, fontSize: fontSizePx, gap: '0.45em', ...style }}
      data-testid={testId}
      data-report-platform-credit="gateway-power"
      data-mark-tone={tone}
    >
      <img
        src={gatewayPowerMarkDataUri(tone)}
        alt=""
        aria-hidden="true"
        width={markPx}
        height={markPx}
        style={{ width: markPx, height: markPx, flex: 'none', display: 'block' }}
        data-testid={`${testId}-mark`}
      />
      <span>{REPORT_PLATFORM_CREDIT_LINE}</span>
    </div>
  );
}
